import {
  NAV_ARRANGEMENT,
  REGISTRY_BLOCK,
  type NavArrangement,
  type RegistryBlockPresentation,
} from '../catalog/platformCatalogIds';
import { NAV_PRESENTATION, type NavPresentation } from './navPresentation';

const P = REGISTRY_BLOCK.presentation;

/**
 * Utility Bar ↔ Navigation behaviour harmony.
 *
 * Sticky Nav Looks (classic · frosted · conversion · editorial) pair with
 * Sticky / Carousel Utility — Utility pins from tablet up; on phone the strip
 * scrolls away (market default) while those sticky Looks keep the pin.
 * Absolute / cinematic Looks (overlay · capsule · quiet · whisper) stay
 * absolute over the hero (not force-pinned) and keep the first viewport clean —
 * Utility scrolls away or collapses to a teaser.
 *
 * Arrangement can refine Look when the open ritual needs a quieter or louder band.
 * Draft-hidden Utility is omitted from the live tree and never offsets Nav.
 */
export const NAV_LOOK_HARMONIZED_UTILITY: Readonly<
  Record<NavPresentation, RegistryBlockPresentation>
> = {
  [NAV_PRESENTATION.classic]: P.utilityBarSticky,
  [NAV_PRESENTATION.frosted]: P.utilityBarSticky,
  [NAV_PRESENTATION.conversion]: P.utilityBarCarousel,
  [NAV_PRESENTATION.editorial]: P.utilityBarSticky,
  [NAV_PRESENTATION.overlay]: P.utilityBarStatic,
  [NAV_PRESENTATION.capsule]: P.utilityBarStatic,
  [NAV_PRESENTATION.quiet]: P.utilityBarCollapse,
  [NAV_PRESENTATION.whisper]: P.utilityBarCollapse,
};

/**
 * Arrangement refinements — win over Look when both are known.
 * Curtain ceremony / Signal social drawer stay quieter; Ledger stays conversion-loud.
 */
export const NAV_ARRANGEMENT_HARMONIZED_UTILITY: Readonly<
  Partial<Record<NavArrangement, RegistryBlockPresentation>>
> = {
  [NAV_ARRANGEMENT.curtain]: P.utilityBarStatic,
  [NAV_ARRANGEMENT.signal]: P.utilityBarCollapse,
  [NAV_ARRANGEMENT.ledger]: P.utilityBarCarousel,
};

/** Short labels for stage-strip / Axes copy. */
export const UTILITY_ARRANGEMENT_SHORT_LABELS: Readonly<
  Record<string, string>
> = {
  [P.utilityBarStatic]: 'Static',
  [P.utilityBarSticky]: 'Sticky',
  [P.utilityBarCarousel]: 'Carousel',
  [P.utilityBarCollapse]: 'Collapse',
};

/**
 * Recommended Utility Arrangement for the current Nav Look + Arrangement.
 * Arrangement override wins when present; else Look; default Sticky.
 */
export function resolveNavHarmonizedUtilityPresentation(
  navLook?: NavPresentation | null,
  navArrangement?: NavArrangement | null,
): RegistryBlockPresentation {
  if (navArrangement && NAV_ARRANGEMENT_HARMONIZED_UTILITY[navArrangement]) {
    return NAV_ARRANGEMENT_HARMONIZED_UTILITY[navArrangement]!;
  }
  if (navLook && NAV_LOOK_HARMONIZED_UTILITY[navLook]) {
    return NAV_LOOK_HARMONIZED_UTILITY[navLook];
  }
  return P.utilityBarSticky;
}

/**
 * True when Utility Arrangement can pin (Sticky / Carousel).
 * Runtime pin is tablet+ (`site.css` @container); phone always scrolls away.
 */
export function isUtilityPinnedPresentation(
  registryBlockKey?: string | null,
): boolean {
  return (
    registryBlockKey === P.utilityBarSticky ||
    registryBlockKey === P.utilityBarCarousel
  );
}
